import { isSwappedOrigin } from '@funkit/connect-core'

/**
 * Swapped's payment service provider. A card payment leaves the Swapped origin
 * for BridgerPay's hosted checkout and its 3DS challenge
 * (`mpi-checkout.bridgerpay.com`), so the journey is not confined to one host.
 */
const PSP_DOMAINS = ['bridgerpay.com'] as const

/**
 * Apex and `www` are Swapped's marketing site (Terms of Use, support). They are
 * Swapped origins, but they are not the widget — following one replaces the
 * embed and its only back/close controls with a page that has no way home.
 */
const SWAPPED_MARKETING_HOSTS = [
  'swapped.com',
  'www.swapped.com',
  'swapped.dev',
  'www.swapped.dev',
]

/**
 * Whether a top-frame navigation belongs to the payment journey and must stay
 * inside the WebView, rather than being handed to the system browser.
 *
 * The rule is an explicit allowlist, not "anything the page asks for": this is
 * a payment surface, so an open redirect out of the embed must not be able to
 * put an arbitrary origin behind the widget's chrome.
 *
 * Three things stay:
 * - the embed's own origin;
 * - any other Swapped host (`*.swapped.com` / `.dev`) except the marketing
 *   site — the flow redirects to a per-partner host such as
 *   `northstake.swapped.com` after login;
 * - the PSP that runs the card charge and hosts the 3DS challenge.
 *
 * The issuer's ACS challenge is deliberately absent: its host is chosen by the
 * cardholder's bank and cannot be enumerated. It does not need to be — the PSP
 * renders it in an iframe, and subframes never reach this gate.
 */
export function staysInWebView(
  target: string,
  embedOrigin: string | null,
): boolean {
  let hostname: string
  let origin: string
  try {
    const parsed = new URL(target)
    // Anything but https is not part of the journey: an http hop is a
    // downgrade, and a custom scheme is a hand-off to another app.
    if (parsed.protocol !== 'https:') {
      return false
    }
    hostname = parsed.hostname.toLowerCase()
    origin = parsed.origin
  } catch {
    return false
  }

  if (embedOrigin != null && origin === embedOrigin) {
    return true
  }
  if (isSwappedOrigin(origin) && !SWAPPED_MARKETING_HOSTS.includes(hostname)) {
    return true
  }
  return PSP_DOMAINS.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  )
}
